import type { AttachmentDto, ConsignmentDto, DriverDto, EWayBillListItemDto, ListQuery, LorryHireChallanDto, PagedResult, TripDto, VehicleDto } from "../types/api";
import { apiClient, getDefaultContext } from "./httpClient";
import { buildMasterDataKey, getCachedMasterData, invalidateMasterDataTags } from "./masterDataCache";

function toQuery(params?: ListQuery, defaultSort = "id") {
  const page = params?.page ?? 1;
  const pageSize = params?.pageSize ?? 20;
  const start = (page - 1) * pageSize;
  const end = start + pageSize;
  return {
    _start: start,
    _end: end,
    _sort: params?.sort ?? defaultSort,
    _order: params?.order ?? "DESC",
    q: params?.q,
    tripId: params?.tripId,
    vehicleId: params?.vehicleId
  };
}

async function listResource<T>(resource: string, params?: ListQuery, defaultSort = "id"): Promise<PagedResult<T>> {
  const response = await apiClient.get<T[]>(`/${resource}`, { params: toQuery(params, defaultSort) });
  return {
    data: response.data,
    total: Number(response.headers["x-total-count"] ?? response.data.length)
  };
}

type LookupResult = { items: Array<{ id: number; label: string; subtitle?: string }>; total: number };
type MasterDataCacheOptions = { forceRefresh?: boolean };

export type PartyMasterRecord = {
  id: number;
  name: string;
  code: string;
  type: string;
  gstNumber?: string | null;
  pan?: string | null;
  aadhaarNumber?: string | null;
  contactPerson?: string | null;
  email?: string | null;
  phone?: string | null;
  addressLine1?: string | null;
  city?: string | null;
  state?: string | null;
  registrationType?: string | null;
  country?: string | null;
  landmark?: string | null;
  pincode?: string | null;
  openingBalance?: number;
  openingBalanceType?: string | null;
  faxNo?: string | null;
  website?: string | null;
  note?: string | null;
  bankAccounts?: Array<{
    bankName?: string;
    branchName?: string | null;
    ifsc?: string | null;
    accountNumber?: string;
    accountHolderName?: string;
    isPrimary?: boolean;
  }>;
  isActive: boolean;
};

export type PartyReceiptMasterRecord = Pick<PartyMasterRecord,
  "id" | "name" | "code" | "type" | "gstNumber" | "pan" | "aadhaarNumber" | "contactPerson" | "phone" | "isActive"
>;

export type CreatePartyLookupPayload = {
  name: string;
  code?: string;
  type?: string;
  gstNumber?: string;
  pan?: string;
  aadhaarNumber?: string;
  contactPerson?: string;
  phone?: string;
};

// ── Gate Pass types ───────────────────────────────────────────────────────────

export type GatePassSummary = {
  id: number;
  gatePassNo: string;
  entryDate: string;
  deliveryDate?: string;
  consignmentNo?: string;
  deliveredBy?: string;
};

export type AuditEntryDto = {
  id:            number;
  module:        string;
  entityType:    string;
  entityId:      string;
  action:        string;
  changedFields?: string;
  oldValuesJson?: string;
  newValuesJson?: string;
  branchId?:     number;
  branchName?:   string;
  userId:        string;
  userName:      string;
  ipAddress?:    string;
  timestamp:     string;
};

export type EligibleConsignmentDto = {
  id: number;
  consignmentNo: string;
  bookingDate: string;
  origin?: string;
  destination?: string;
  consignorName?: string;
  consigneeName?: string;
  totalPackages: number;
  totalWeight: number;
  paymentType: string;
};

export type CreateGatePassPayload = {
  // GatePassNo is NOT accepted — allocated server-side from stationery.
  branchId?: number;       // null = JWT branch for regular users; required for admins
  consignmentId: number;
  manifestId?: number;
  deliveredBy: string;
  deliveryDate: string;
  remarks?: string;
  tempKey?: string | null;
};

const MASTER_DATA_TAGS = {
  branches: "master:branches",
  branchLookups: "master:branch-lookups",
  parties: "master:parties",
  partyLookups: "master:party-lookups",
  vehicles: "master:vehicles",
  vehicleLookups: "master:vehicle-lookups",
  drivers: "master:drivers",
  driverLookups: "master:driver-lookups",
  trips: "master:trips",
  tripLookups: "master:trip-lookups",
  locations: "master:locations",
  financialYears: "master:financial-years",
  activeFinancialYear: "master:active-financial-year",
  accountingSettings: "master:accounting-settings",
  transportTaxProfiles: "master:transport-tax-profiles",
  sacTaxProfiles: "master:sac-tax-profiles",
  documentSequences: "master:document-sequences"
} as const;

export type BiltyNumberSeriesIssueDto = {
  id: number;
  documentType: "Consignment" | "Manifest" | "GatePass";
  issueBranchId: number;
  issueBranchName: string;
  assignedBranchId: number;
  assignedBranchName: string;
  issueDate: string;
  challanNo: string;
  itemCode: string;
  itemName: string;
  quantity: number;
  unitType: string;
  numbersPerBook: number;
  startNumber: number;
  endNumber: number;
  totalNumbers: number;
  usedCount: number;
  availableCount: number;
  isActive: boolean;
  remarks?: string;
};

export type DocumentNumberInventoryDto = {
  documentType: "Consignment" | "Manifest" | "GatePass";
  activeRangeCount: number;
  historicalRangeCount: number;
  assignedCount: number;
  usedCount: number;
  pendingCount: number;
  historicalAssignedCount: number;
  historicalUsedCount: number;
  branches: Array<{
    branchId: number; branchName: string; rangeCount: number;
    assignedCount: number; usedCount: number; pendingCount: number;
  }>;
};

export type DocumentSequenceNumbersDto = {
  issueId: number;
  documentType: "Consignment" | "Manifest" | "GatePass";
  assignedBranchName: string;
  startNumber: number;
  endNumber: number;
  status: "all" | "used" | "pending";
  page: number;
  pageSize: number;
  total: number;
  items: Array<{
    number: number;
    status: "Used" | "Pending";
    issueId: number;
    assignedBranchId: number;
    assignedBranchName: string;
    documentId?: number;
    parentDocumentId?: number;
  }>;
};

function requireSequenceLedger(payload: unknown): DocumentSequenceNumbersDto {
  if (!payload || typeof payload !== "object" || !Array.isArray((payload as DocumentSequenceNumbersDto).items)) {
    throw new Error("The sequence ledger API returned an invalid response.");
  }
  return payload as DocumentSequenceNumbersDto;
}

export type GstinLookupResult = {
  found: boolean;
  name?: string;
  legalName?: string;
  address?: string;
  status?: string;
  constitution?: string;
  source?: string;
};

export const transportApi = {
  // Consignments — list via resource query controller, mutations via transport controller
  listConsignments: (params?: ListQuery) => listResource<ConsignmentDto>("consignments", params),
  getConsignment: async (id: number) => (await apiClient.get<ConsignmentDto>(`/transport/consignments/${id}`)).data,
  createConsignment: async (payload: any) => (await apiClient.post<ConsignmentDto>("/transport/consignments", payload)).data,
  updateConsignment: async (id: number, payload: any) => apiClient.put(`/transport/consignments/${id}`, payload),
  cancelConsignment: async (id: number, cancelledBy: string) => {
    const ctx = getDefaultContext();
    await apiClient.post(`/transport/consignments/${id}/cancel`, { ...ctx, cancelledBy });
  },

  listBiltyNumberSeries: async (params?: ListQuery & { branchId?: number; documentType?: string }): Promise<PagedResult<BiltyNumberSeriesIssueDto>> => {
    const response = await apiClient.get<BiltyNumberSeriesIssueDto[]>("/bilty-number-series", {
      params: { ...toQuery(params, "id"), branchId: params?.branchId, documentType: params?.documentType }
    });
    return {
      data: response.data,
      total: Number(response.headers["x-total-count"] ?? response.data.length)
    };
  },
  listAvailableBiltyNumberSeries: async (
    branchId: number,
    q?: string,
    documentType = "Consignment",
    options?: { includeUsage?: boolean; signal?: AbortSignal }
  ) =>
    (await apiClient.get<BiltyNumberSeriesIssueDto[]>("/bilty-number-series/available", {
      params: { branchId, q, documentType, includeUsage: options?.includeUsage },
      signal: options?.signal
    })).data,
  createBiltyNumberSeries: async (payload: any) =>
    (await apiClient.post<BiltyNumberSeriesIssueDto>("/bilty-number-series", payload)).data,
  listHeadOfficeStationeryReceipts: async () =>
    (await apiClient.get<any[]>("/head-office-stationery-receipts")).data,
  listStationeryPrinters: async () =>
    (await apiClient.get<any[]>("/head-office-stationery-receipts/printers")).data,
  createHeadOfficeStationeryReceipt: async (payload: any) =>
    (await apiClient.post("/head-office-stationery-receipts", payload)).data,
  listHeadOfficeStationeryInventoryLines: async () =>
    (await apiClient.get<any[]>("/head-office-stationery-receipts/inventory-lines")).data,
  assignHeadOfficeStationeryToBranch: async (payload: { receiptLineIds: number[]; branchId: number; challanNo?: string; remarks?: string }) =>
    (await apiClient.post("/head-office-stationery-receipts/assign-to-branch", payload)).data,
  updateBiltyNumberSeries: async (id: number, payload: any) =>
    (await apiClient.put<BiltyNumberSeriesIssueDto>(`/bilty-number-series/${id}`, payload)).data,
  deactivateBiltyNumberSeries: async (id: number) =>
    apiClient.post(`/bilty-number-series/${id}/deactivate`, {}),
  getDocumentNumberInventory: async (branchId?: number) =>
    (await apiClient.get<DocumentNumberInventoryDto[]>("/bilty-number-series/summary", { params: { branchId } })).data,
  getDocumentSequenceNumbers: async (id: number, params?: { status?: "all" | "used" | "pending"; q?: string; page?: number; pageSize?: number }) =>
    requireSequenceLedger((await apiClient.get<unknown>(`/bilty-number-series/${id}/numbers`, { params })).data),
  getDocumentSequenceNumbersByType: async (documentType: "Consignment" | "Manifest" | "GatePass", params?: { branchId?: number; status?: "all" | "used" | "pending"; q?: string; page?: number; pageSize?: number }) =>
    requireSequenceLedger((await apiClient.get<unknown>("/bilty-number-series/numbers", { params: { documentType, ...params } })).data),
  getNextDocumentNumber: async (documentType: "Consignment" | "Manifest" | "GatePass", branchId: number): Promise<{ nextNumber: number | null; message?: string }> =>
    (await apiClient.get<{ nextNumber: number | null; message?: string }>("/bilty-number-series/next-number", { params: { documentType, branchId } })).data,

  // Trips
  listTrips: (params?: ListQuery) => listResource<TripDto>("trips", params),
  createTrip: async (payload: any) => {
    const result = (await apiClient.post<TripDto>("/transport/trips", payload)).data;
    invalidateMasterDataTags([MASTER_DATA_TAGS.trips, MASTER_DATA_TAGS.tripLookups]);
    return result;
  },
  completeTrip: async (id: number, payload: any) => {
    const result = await apiClient.post(`/transport/trips/${id}/complete`, payload);
    invalidateMasterDataTags([MASTER_DATA_TAGS.trips, MASTER_DATA_TAGS.tripLookups]);
    return result;
  },
  getTrip: async (organizationId: number, financialYearId: number, tripId: number) =>
    (await apiClient.get<TripDto>(`/transport/trips/${organizationId}/${financialYearId}/${tripId}`)).data,

  // Vehicles
  listVehicles: (params?: ListQuery) => listResource<VehicleDto>("vehicles", params),
  createVehicle: async (payload: any) => {
    const result = (await apiClient.post<VehicleDto>("/transport/vehicles", payload)).data;
    invalidateMasterDataTags([MASTER_DATA_TAGS.vehicles, MASTER_DATA_TAGS.vehicleLookups]);
    return result;
  },
  updateVehicle: async (id: number, payload: any) => {
    const result = (await apiClient.put<VehicleDto>(`/transport/vehicles/${id}`, payload)).data;
    invalidateMasterDataTags([MASTER_DATA_TAGS.vehicles, MASTER_DATA_TAGS.vehicleLookups]);
    return result;
  },
  getActiveVehicles: async (organizationId: number, financialYearId: number) =>
    (await apiClient.get<VehicleDto[]>(`/transport/vehicles/${organizationId}/${financialYearId}`)).data,
  getVehicleDetail: async (id: number) =>
    (await apiClient.get<{ id: number; vehicleNo: string; ownerName: string }>(`/vehicles/detail/${id}`)).data,

  // Drivers
  listDrivers: (params?: ListQuery) => listResource<DriverDto>("drivers", params),
  createDriver: async (payload: any) => {
    const result = (await apiClient.post<DriverDto>("/transport/drivers", payload)).data;
    invalidateMasterDataTags([MASTER_DATA_TAGS.drivers, MASTER_DATA_TAGS.driverLookups]);
    return result;
  },
  updateDriver: async (id: number, payload: any) => {
    const result = (await apiClient.put<DriverDto>(`/transport/drivers/${id}`, payload)).data;
    invalidateMasterDataTags([MASTER_DATA_TAGS.drivers, MASTER_DATA_TAGS.driverLookups]);
    return result;
  },
  deleteDriver: async (id: number) => {
    const result = await apiClient.delete(`/drivers/${id}`);
    invalidateMasterDataTags([MASTER_DATA_TAGS.drivers, MASTER_DATA_TAGS.driverLookups]);
    return result;
  },
  getActiveDrivers: async (organizationId: number, financialYearId: number) =>
    (await apiClient.get<DriverDto[]>(`/transport/drivers/${organizationId}/${financialYearId}`)).data,

  // Manifests
  createManifest: async (payload: any) => (await apiClient.post("/transport/manifests", payload)).data,
  listEligibleManifestConsignments: async (fromBranchId: number, signal?: AbortSignal) =>
    (await apiClient.get<any[]>("/transport/manifests/eligible-consignments", { params: { fromBranchId }, signal })).data,
  getNextAutomaticManifestNumber: async (fromBranchId: number, signal?: AbortSignal) =>
    (await apiClient.get<{ number: string }>("/transport/manifests/next-automatic-number", { params: { fromBranchId }, signal })).data,
  dispatchManifest: async (manifestId: number, payload: any) => apiClient.post(`/transport/manifests/${manifestId}/dispatch`, payload),
  getManifest: async (id: number) => (await apiClient.get(`/transport/manifests/${id}`)).data,
  requestManifestEdit: async (id: number, reason: string) => (await apiClient.post(`/transport/manifests/${id}/request-edit`, { reason })).data,
  approveManifestEdit: async (id: number) => (await apiClient.post(`/transport/manifests/${id}/approve-edit`, {})).data,
  updateApprovedManifest: async (id: number, payload: any) => apiClient.put(`/transport/manifests/${id}/approved-edit`, payload),

  // Manifest Arrivals
  listManifestArrivals: (params?: ListQuery) => listResource<any>("manifest-arrivals", params),
  getManifestArrival: async (id: number) => (await apiClient.get<any>(`/manifest-arrivals/${id}`)).data,
  lookupManifestForArrival: async (challanNo: string) =>
    (await apiClient.get<any>("/manifest-arrivals/lookup-manifest", { params: { challanNo } })).data,
  searchManifestSuggestions: async (q: string) =>
    (await apiClient.get<any[]>("/manifest-arrivals/search-manifests", { params: { q } })).data,
  lookupManifestByConsignment: async (consignmentNo: string) =>
    (await apiClient.get<any>("/manifest-arrivals/lookup-by-consignment", { params: { consignmentNo } })).data,
  createManifestArrival: async (payload: any) => (await apiClient.post<any>("/manifest-arrivals", payload)).data,
  updateManifestArrival: async (id: number, payload: any) =>
    (await apiClient.put<any>(`/manifest-arrivals/${id}`, payload)).data,
  deleteManifestArrival: async (id: number) => apiClient.delete(`/manifest-arrivals/${id}`),
  processManifestArrival: async (id: number, processedBy?: string) =>
    (await apiClient.post<any>(`/manifest-arrivals/${id}/process`, { processedBy })).data,

  // Deliveries
  listDeliveries: (params?: ListQuery) => listResource<any>("deliveries", params),
  createDelivery: async (payload: any) => (await apiClient.post("/transport/deliveries", payload)).data,
  getDeliveryByConsignment: async (organizationId: number, financialYearId: number, consignmentId: number) =>
    (await apiClient.get(`/transport/deliveries/${organizationId}/${financialYearId}/${consignmentId}`)).data,

  // Fleet operations
  listFuelLogs: (params?: ListQuery) => listResource<any>("fuel-logs", params),
  listMaintenances: (params?: ListQuery) => listResource<any>("maintenances", params),
  listTripExpenses: (params?: ListQuery) => listResource<any>("trip-expenses", params),
  listManifests: (params?: ListQuery) => listResource<any>("manifests", params),
  addFuelLog: async (payload: any) => (await apiClient.post("/transport/fuel", payload)).data,
  addMaintenance: async (payload: any) => (await apiClient.post("/transport/maintenance", payload)).data,
  addTripExpense: async (payload: any) => (await apiClient.post("/transport/trip-expenses", payload)).data,

  // Tracking
  getTrackingTimeline: async (organizationId: number, financialYearId: number, consignmentId: number) =>
    (await apiClient.get(`/transport/tracking/${organizationId}/${financialYearId}/${consignmentId}`)).data,

  // Attachments
  listAttachments: async (params?: ListQuery & { branchId?: number; entityType?: string; entityId?: number; documentType?: string; includeDeleted?: boolean }) => {
    const page = params?.page ?? 1;
    const pageSize = params?.pageSize ?? 20;
    const start = (page - 1) * pageSize;
    const response = await apiClient.get<AttachmentDto[]>("/attachments", {
      params: {
        _start: start, _end: start + pageSize,
        _sort: params?.sort ?? "uploadedAt", _order: params?.order ?? "DESC",
        q: params?.q || undefined,
        branchId: params?.branchId,
        entityType: params?.entityType,
        entityId: params?.entityId,
        documentType: params?.documentType,
        includeDeleted: params?.includeDeleted
      }
    });
    return {
      data: response.data,
      total: Number(response.headers["x-total-count"] ?? response.data.length)
    };
  },
  uploadAttachment: async (payload: {
    module: string;
    entityType: string;
    entityId: number;
    documentType: string;
    branchId?: number;
    branchCode?: string;
    tagsJson?: string;
    tempKey?: string | null;
    file: File;
    /** Called repeatedly during upload with 0-100 percent value */
    onUploadProgress?: (percent: number) => void;
  }) => {
    const formData = new FormData();
    formData.append("module", payload.module);
    formData.append("entityType", payload.entityType);
    formData.append("entityId", String(payload.entityId));
    formData.append("documentType", payload.documentType);
    if (payload.branchId != null) formData.append("branchId", String(payload.branchId));
    if (payload.branchCode) formData.append("branchCode", payload.branchCode);
    if (payload.tagsJson) formData.append("tagsJson", payload.tagsJson);
    if (payload.tempKey) formData.append("tempKey", payload.tempKey);
    formData.append("file", payload.file);
    return (await apiClient.post<string>("/attachments/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: payload.onUploadProgress
        ? (evt) => {
            if (evt.total) {
              payload.onUploadProgress!(Math.min(99, Math.round((evt.loaded / evt.total) * 100)));
            }
          }
        : undefined,
    })).data;
  },
  deleteAttachment: async (id: string) => apiClient.delete(`/attachments/${id}`),
  updateAttachmentMetadata: async (id: string, payload: { documentType: string; tagsJson?: string }) =>
    apiClient.patch(`/attachments/${id}/metadata`, payload),
  listZombieAttachments: async () =>
    (await apiClient.get<import("../types/api").AttachmentZombieDto[]>("/attachments/admin/zombies")).data,
  purgeZombieAttachments: async () =>
    (await apiClient.post<{ purged: number; message: string }>("/attachments/admin/purge")).data,
  linkAttachment: async (payload: {
    attachmentId: string;
    module: string;
    entityType: string;
    entityId: number;
    documentType: string;
    tagsJson?: string;
  }) => apiClient.post("/attachments/link", payload),
  getAttachmentsByEntity: async (module: string, entityType: string, entityId: number) =>
    (await apiClient.get<AttachmentDto[]>("/attachments/by-entity", { params: { module, entityType, entityId } })).data,
  getAttachmentsByType: async (module: string, documentType: string) =>
    (await apiClient.get<AttachmentDto[]>("/attachments/by-type", { params: { module, documentType } })).data,
  getAttachmentsByTempKey: async (tempKey: string) =>
    (await apiClient.get<AttachmentDto[]>("/attachments/by-temp-key", { params: { tempKey } })).data,
  deleteAttachmentsByTempKey: async (tempKey: string) =>
    apiClient.delete(`/attachments/temp/${tempKey}`),
  bindTempAttachments: async (tempKey: string, entityType: string, entityId: number) =>
    apiClient.post(`/attachments/bind-temp`, { tempKey, entityType, entityId }),

  // Lookups — parties
  searchPartyLookups: async (q: string, page = 1, pageSize = 20, options?: MasterDataCacheOptions) =>
    getCachedMasterData({
      key: buildMasterDataKey("party-lookups-v2", { q, page, pageSize }),
      tags: [MASTER_DATA_TAGS.partyLookups],
      scope: "tenant",
      forceRefresh: options?.forceRefresh,
      revalidateCached: (result) => q.trim().length > 0 && result.items.length === 0,
      fetcher: async () => (await apiClient.get<LookupResult>("/lookups/parties", { params: { q, page, pageSize } })).data
    }),
  createPartyLookup: async (payload: CreatePartyLookupPayload) => {
    const result = (await apiClient.post<{ id: number }>("/parties", payload)).data;
    invalidateMasterDataTags([MASTER_DATA_TAGS.parties, MASTER_DATA_TAGS.partyLookups]);
    return {
      id: result.id,
      label: payload.name,
      subtitle: payload.type || "Customer",
    };
  },

  // Lookups — consignments
  searchConsignmentLookups: async (q: string, page = 1, pageSize = 20) =>
    (await apiClient.get<LookupResult>("/lookups/consignments", { params: { q, page, pageSize } })).data,

  // Lookups — vehicles
  searchVehicleLookups: async (q = "", pageSize = 50, options?: MasterDataCacheOptions) =>
    getCachedMasterData({
      key: buildMasterDataKey("vehicle-lookups", { q, pageSize }),
      tags: [MASTER_DATA_TAGS.vehicleLookups],
      scope: "tenant",
      forceRefresh: options?.forceRefresh,
      fetcher: async () => (await apiClient.get<LookupResult>("/lookups/vehicles", { params: { q, pageSize } })).data
    }),

  // Lookups — drivers
  searchDriverLookups: async (q = "", pageSize = 50, options?: MasterDataCacheOptions) =>
    getCachedMasterData({
      key: buildMasterDataKey("driver-lookups", { q, pageSize }),
      tags: [MASTER_DATA_TAGS.driverLookups],
      scope: "tenant",
      forceRefresh: options?.forceRefresh,
      fetcher: async () => (await apiClient.get<LookupResult>("/lookups/drivers", { params: { q, pageSize } })).data
    }),

  // Lookups — trips
  searchTripLookups: async (q = "", pageSize = 30, activeOnly?: boolean, options?: MasterDataCacheOptions) =>
    getCachedMasterData({
      key: buildMasterDataKey("trip-lookups", { q, pageSize, activeOnly }),
      tags: [MASTER_DATA_TAGS.tripLookups],
      scope: "tenant-fy",
      forceRefresh: options?.forceRefresh,
      fetcher: async () => (await apiClient.get<LookupResult>("/lookups/trips", { params: { q, pageSize, activeOnly } })).data
    }),

  // Lookups — branches
  searchBranchLookups: async (q = "", page = 1, pageSize = 50, options?: MasterDataCacheOptions) =>
    getCachedMasterData({
      key: buildMasterDataKey("branch-lookups", { q, page, pageSize }),
      tags: [MASTER_DATA_TAGS.branchLookups],
      scope: "tenant",
      forceRefresh: options?.forceRefresh,
      revalidateCached: (result) => q.trim().length > 0 && result.items.length === 0,
      fetcher: async () => (await apiClient.get<LookupResult>("/lookups/branches", { params: { q, page, pageSize } })).data
    }),
  createBranchLookup: async (payload: { branchName: string; branchCode?: string; city?: string; state?: string }) => {
    const result = (await apiClient.post<{ id: number; label: string; subtitle?: string }>("/lookups/branches", payload)).data;
    invalidateMasterDataTags([MASTER_DATA_TAGS.branches, MASTER_DATA_TAGS.branchLookups, MASTER_DATA_TAGS.locations]);
    return result;
  },

  // Lookups — location suggestions (distinct cities from consignments + branches)
  searchLocationSuggestions: async (q = "", options?: MasterDataCacheOptions) =>
    getCachedMasterData({
      key: buildMasterDataKey("locations", { q }),
      tags: [MASTER_DATA_TAGS.locations],
      scope: "tenant",
      forceRefresh: options?.forceRefresh,
      fetcher: async () => (await apiClient.get<string[]>("/lookups/locations", { params: { q } })).data
    }),

  // Branches — full CRUD
  listBranches: (params?: ListQuery) =>
    getCachedMasterData({
      key: buildMasterDataKey("branches", params as Record<string, unknown> | undefined),
      tags: [MASTER_DATA_TAGS.branches],
      scope: "tenant",
      fetcher: async () => listResource<any>("branches", params)
    }),
  getHeadOffice: async () => (await apiClient.get<{ id: number; branchCode: string; branchName: string }>("/branches/head-office")).data,
  setHeadOffice: async (id: number) => {
    const result = (await apiClient.post<{ id: number; branchCode: string; branchName: string }>(`/branches/${id}/set-head-office`, {})).data;
    invalidateMasterDataTags([MASTER_DATA_TAGS.branches, MASTER_DATA_TAGS.branchLookups]);
    return result;
  },
  createBranch: async (payload: any) => {
    const result = (await apiClient.post<any>("/branches", payload)).data;
    invalidateMasterDataTags([MASTER_DATA_TAGS.branches, MASTER_DATA_TAGS.branchLookups, MASTER_DATA_TAGS.locations]);
    return result;
  },
  updateBranch: async (id: number, payload: any) => {
    const result = (await apiClient.put<any>(`/branches/${id}`, payload)).data;
    invalidateMasterDataTags([MASTER_DATA_TAGS.branches, MASTER_DATA_TAGS.branchLookups, MASTER_DATA_TAGS.locations]);
    return result;
  },
  deleteBranch: async (id: number) => {
    const result = await apiClient.delete(`/branches/${id}`);
    invalidateMasterDataTags([MASTER_DATA_TAGS.branches, MASTER_DATA_TAGS.branchLookups, MASTER_DATA_TAGS.locations]);
    return result;
  },

  // Vehicle odometer
  getVehicleLastOdometer: async (vehicleId: number) =>
    (await apiClient.get<{ vehicleId: number; lastOdometer: number | null }>(`/lookups/vehicles/${vehicleId}/odometer`)).data,

  // E-way Bill
  getEWayBillByConsignment: async (consignmentId: number) =>
    (await apiClient.get(`/transport/ewaybills/by-consignment/${consignmentId}`)).data,
  generateEWayBill: async (payload: {
    organizationId: number; financialYearId: number; consignmentId: number;
    eWayBillNo: string; generatedOn?: string; validUpto: string; generatedBy: string;
    transportMode?: string; currentVehicleNo?: string; currentFromPlace?: string; remainingDistanceKm?: number;
  }) => (await apiClient.post("/transport/ewaybills/generate", payload)).data,
  cancelEWayBill: async (id: number, payload: { organizationId: number; financialYearId: number; reason: string }) =>
    apiClient.post(`/transport/ewaybills/${id}/cancel`, payload),
  getEWayBillTimeline: async (id: number) =>
    (await apiClient.get(`/transport/ewaybills/${id}/timeline`)).data,
  getEWayBillDashboard: async () =>
    (await apiClient.get(`/transport/ewaybills/dashboard`)).data,
  listEWayBills: async (query?: { q?: string; page?: number; pageSize?: number }) =>
    (await apiClient.get<{ items: EWayBillListItemDto[]; total: number }>(`/transport/ewaybills`, { params: query })).data,
  markEWayBillDelivered: async (id: number) =>
    apiClient.post(`/transport/ewaybills/${id}/mark-delivered`, {}),
  enableEWayBillAutoExtension: async (id: number) =>
    apiClient.post(`/transport/ewaybills/${id}/enable-auto-extension`, {}),
  disableEWayBillAutoExtension: async (id: number, note?: string) =>
    apiClient.post(`/transport/ewaybills/${id}/disable-auto-extension`, { note }),
  updateEWayBillDistance: async (id: number, distanceKm: number) =>
    apiClient.post(`/transport/ewaybills/${id}/update-distance`, { distanceKm }),

  /** Manually extend an EWB via NIC API with operator-supplied details. */
  extendEWayBill: async (id: number, payload: {
    vehicleNo: string;
    fromPlace: string;
    remainingDistanceKm: number;
    reasonCode?: number;
    remarks?: string;
    extendedBy?: string;
  }) => (await apiClient.post<{ validUpto: string; extensionCount: number }>(
    `/transport/ewaybills/${id}/extend`, payload)).data,

  /** Extend an EWB using GPS coordinates (device/system-triggered). */
  extendEWayBillByGps: async (id: number, payload: {
    latitude: number;
    longitude: number;
    vehicleNo: string;
    fromPlace?: string;
    extendedBy?: string;
  }) => (await apiClient.post<{ validUpto: string; extensionCount: number }>(
    `/transport/ewaybills/${id}/extend/gps`, payload)).data,

  /** Update vehicle number on an active EWB (e.g. transshipment). */
  updateEWayBillVehicle: async (id: number, payload: {
    newVehicleNo: string;
    fromPlace: string;
    transportMode?: number;
    remarks?: string;
    updatedBy?: string;
  }) => apiClient.post(`/transport/ewaybills/${id}/update-vehicle`, payload),

  /** Get current NIC API environment (Sandbox / Production). */
  getEWayBillEnvironment: async (): Promise<{ isSandbox: boolean; environment: string; baseUrl: string }> =>
    (await apiClient.get(`/transport/ewaybills/environment`)).data,

  // Parties — full CRUD
  listParties: (params?: ListQuery) =>
    getCachedMasterData({
      key: buildMasterDataKey("parties", params as Record<string, unknown> | undefined),
      tags: [MASTER_DATA_TAGS.parties],
      scope: "tenant",
      fetcher: async () => listResource<any>("parties", params)
      }),
  getPartyReceiptDetails: async (id: number) => getCachedMasterData<PartyReceiptMasterRecord>({
    key: buildMasterDataKey("party-detail", { id }),
    tags: [MASTER_DATA_TAGS.parties, MASTER_DATA_TAGS.partyLookups],
    scope: "tenant",
    fetcher: async () => {
      const party = (await apiClient.get<PartyMasterRecord>(`/parties/${id}`)).data;
      return {
        id: party.id,
        name: party.name,
        code: party.code,
        type: party.type,
        gstNumber: party.gstNumber,
        pan: party.pan,
        aadhaarNumber: party.aadhaarNumber,
        contactPerson: party.contactPerson,
        phone: party.phone,
        isActive: party.isActive,
      };
    },
  }),
  getPartyFull: async (id: number) => (await apiClient.get<PartyMasterRecord>(`/parties/${id}`)).data,
  createPartyFull: async (payload: any) => {
    const result = (await apiClient.post<any>("/parties", payload)).data;
    invalidateMasterDataTags([MASTER_DATA_TAGS.parties, MASTER_DATA_TAGS.partyLookups]);
    return result;
  },
  updatePartyFull: async (id: number, payload: any) => {
    const result = (await apiClient.put<any>(`/parties/${id}`, payload)).data;
    invalidateMasterDataTags([MASTER_DATA_TAGS.parties, MASTER_DATA_TAGS.partyLookups]);
    return result;
  },
  deleteParty: async (id: number) => {
    const result = await apiClient.delete(`/parties/${id}`);
    invalidateMasterDataTags([MASTER_DATA_TAGS.parties, MASTER_DATA_TAGS.partyLookups]);
    return result;
  },
  checkGstin: async (gstin: string) =>
    (await apiClient.get<GstinLookupResult>("/gstin-lookup/check", { params: { gstin } })).data,
  getGstinCaptcha: async () =>
    (await apiClient.get<{ sessionId: string; captchaImage: string }>("/gstin-lookup/captcha")).data,
  lookupGstin: async (payload: { gstin: string; captcha: string; sessionId: string }) =>
    (await apiClient.post<GstinLookupResult>("/gstin-lookup", payload)).data,

  // Accounting engine
  getAccountingSettings: async () =>
    (await apiClient.get<any>("/accounting/settings", { params: { _ts: Date.now() } })).data,
  updateAccountingSettings: async (payload: any) => {
    const result = (await apiClient.put<any>("/accounting/settings", payload)).data;
    invalidateMasterDataTags([MASTER_DATA_TAGS.accountingSettings]);
    return result;
  },
  updateDocumentNumberingSettings: async (payload: {
    isManualBiltyNumberEnabled: boolean;
    isManualManifestNumberEnabled: boolean;
    isManualGatePassNumberEnabled: boolean;
  }) => {
    const result = (await apiClient.put<any>("/accounting/settings/document-numbering", payload)).data;
    invalidateMasterDataTags([MASTER_DATA_TAGS.accountingSettings]);
    return result;
  },
  listPostingProfiles: async () => (await apiClient.get<any[]>("/accounting/profiles")).data,
  bootstrapStandardCoa: async () =>
    (await apiClient.post<{ message: string }>("/accounting/reports/coa/bootstrap", {})).data,
  getChartOfAccounts: async () =>
    (await apiClient.get<any[]>("/accounting/reports/coa")).data,
  exportChartOfAccounts: async () =>
    (await apiClient.get<Blob>("/accounting/reports/coa/export", { responseType: "blob" })).data,
  getTrialBalance: async (asOf: string) =>
    (await apiClient.get<any>("/accounting/reports/trial-balance", { params: { asOf } })).data,
  getProfitLoss: async (from: string, to: string) =>
    (await apiClient.get<any>("/accounting/reports/profit-loss", { params: { from, to } })).data,
  getBalanceSheet: async (asOf: string) =>
    (await apiClient.get<any>("/accounting/reports/balance-sheet", { params: { asOf } })).data,
  getDayBook: async (from: string, to: string) =>
    (await apiClient.get<any>("/accounting/reports/day-book", { params: { from, to } })).data,
  getGstReport: async (from: string, to: string, returnType = "GSTR1") =>
    (await apiClient.get<any>("/accounting/reports/gst", { params: { from, to, returnType } })).data,

  // Financial Years
  listFinancialYears: async () =>
    getCachedMasterData({
      key: "financial-years",
      tags: [MASTER_DATA_TAGS.financialYears],
      scope: "tenant",
      fetcher: async () => (await apiClient.get<any[]>("/financial-years")).data
    }),
  getActiveFinancialYear: async () =>
    getCachedMasterData({
      key: "active-financial-year",
      tags: [MASTER_DATA_TAGS.activeFinancialYear, MASTER_DATA_TAGS.financialYears],
      scope: "tenant",
      fetcher: async () => (await apiClient.get<any>("/financial-years/active")).data
    }),
  createFinancialYear: async (payload: { code: string; startDate: string; endDate: string; isActive: boolean }) => {
    const result = (await apiClient.post<any>("/financial-years", payload)).data;
    invalidateMasterDataTags([MASTER_DATA_TAGS.financialYears, MASTER_DATA_TAGS.activeFinancialYear]);
    return result;
  },
  updateFinancialYear: async (id: number, payload: { code: string; startDate: string; endDate: string; isActive: boolean }) => {
    const result = (await apiClient.put<any>(`/financial-years/${id}`, payload)).data;
    invalidateMasterDataTags([MASTER_DATA_TAGS.financialYears, MASTER_DATA_TAGS.activeFinancialYear, MASTER_DATA_TAGS.documentSequences]);
    return result;
  },
  activateFinancialYear: async (id: number) => {
    const result = (await apiClient.put<any>(`/financial-years/${id}/activate`, {})).data;
    invalidateMasterDataTags([MASTER_DATA_TAGS.financialYears, MASTER_DATA_TAGS.activeFinancialYear, MASTER_DATA_TAGS.documentSequences, MASTER_DATA_TAGS.tripLookups]);
    return result;
  },

  // Dashboard
  getDashboardStats: async (signal?: AbortSignal) =>
    (await apiClient.get<any>("/dashboard/stats", { signal, timeout: 60000 })).data,

  // User Management
  listAdminUsers: async (query?: { q?: string; page?: number; pageSize?: number }) =>
    (await apiClient.get<any[]>("/admin/users", { params: query })).data,
  listRoles: async () =>
    (await apiClient.get<string[]>("/admin/roles")).data,
  createAdminUser: async (payload: { email: string; password: string; firstName?: string; lastName?: string; branchId?: number; roles: string[]; permissions?: string[] }) =>
    (await apiClient.post<{ id: string; message: string }>("/admin/users", payload)).data,
  updateAdminUser: async (userId: string, payload: { email: string; firstName: string; lastName: string; branchId?: number; roles: string[] }) =>
    (await apiClient.put(`/admin/users/${userId}`, payload)).data,
  getUserPermissions: async (userId: string) =>
    (await apiClient.get<{ userId: string; permissions: string[] }>(`/admin/users/${userId}/permissions`)).data,
  setUserPermissions: async (userId: string, permissions: string[]) =>
    (await apiClient.post(`/admin/users/${userId}/permissions`, { permissions })).data,
  resetUserPassword: async (userId: string, newPassword: string) =>
    (await apiClient.post(`/admin/users/${userId}/reset-password`, { newPassword })).data,
  lockUser: async (userId: string) =>
    (await apiClient.post(`/admin/users/${userId}/lock`, {})).data,
  unlockUser: async (userId: string) =>
    (await apiClient.post(`/admin/users/${userId}/unlock`, {})).data,
  deleteUser: async (userId: string) =>
    (await apiClient.delete(`/admin/users/${userId}`)).data,

  // Document Sequences - Master Data Configuration
  createDocumentSequence: async (payload: { documentType: string; branchCode?: string; prefix: string; padding?: number }) => {
    const result = (await apiClient.post("/document-sequences", payload)).data;
    invalidateMasterDataTags([MASTER_DATA_TAGS.documentSequences]);
    return result;
  },
  updateDocumentSequence: async (id: number, payload: any) => {
    const result = (await apiClient.put(`/document-sequences/${id}`, payload)).data;
    invalidateMasterDataTags([MASTER_DATA_TAGS.documentSequences]);
    return result;
  },
  getDocumentSequenceById: async (id: number) =>
    (await apiClient.get(`/document-sequences/${id}`)).data,
  listDocumentSequencesByFinancialYear: async (financialYearId: number) =>
    getCachedMasterData({
      key: buildMasterDataKey("document-sequences", { financialYearId }),
      tags: [MASTER_DATA_TAGS.documentSequences],
      scope: "tenant",
      fetcher: async () => (await apiClient.get(`/document-sequences/financial-year/${financialYearId}`)).data
    }),
  deactivateDocumentSequence: async (id: number) => {
    const result = (await apiClient.post(`/document-sequences/${id}/deactivate`, {})).data;
    invalidateMasterDataTags([MASTER_DATA_TAGS.documentSequences]);
    return result;
  },

  // Tax Profiles
  listTransportServiceTaxProfiles: async () =>
    getCachedMasterData({
      key: "transport-tax-profiles",
      tags: [MASTER_DATA_TAGS.transportTaxProfiles],
      scope: "tenant",
      fetcher: async () => (await apiClient.get("/tax-profiles/transport-services")).data
    }),
  createTransportServiceTaxProfile: async (payload: { serviceCode: string; sacCode: string; isActive: boolean }) => {
    const result = (await apiClient.post("/tax-profiles/transport-services", payload)).data;
    invalidateMasterDataTags([MASTER_DATA_TAGS.transportTaxProfiles]);
    return result;
  },
  updateTransportServiceTaxProfile: async (id: number, payload: { serviceCode: string; sacCode: string; isActive: boolean }) => {
    const result = (await apiClient.put(`/tax-profiles/transport-services/${id}`, payload)).data;
    invalidateMasterDataTags([MASTER_DATA_TAGS.transportTaxProfiles]);
    return result;
  },
  seedTransportServiceTaxProfiles: async () => {
    const result = (await apiClient.post("/tax-profiles/transport-services/seed-defaults", {})).data;
    invalidateMasterDataTags([MASTER_DATA_TAGS.transportTaxProfiles]);
    return result;
  },

  listSacTaxProfiles: async () =>
    getCachedMasterData({
      key: "sac-tax-profiles",
      tags: [MASTER_DATA_TAGS.sacTaxProfiles],
      scope: "tenant",
      fetcher: async () => (await apiClient.get("/tax-profiles/sac")).data
    }),
  createSacTaxProfile: async (payload: { sacCode: string; cgstRate: number; sgstRate: number; igstRate: number; isActive: boolean }) => {
    const result = (await apiClient.post("/tax-profiles/sac", payload)).data;
    invalidateMasterDataTags([MASTER_DATA_TAGS.sacTaxProfiles]);
    return result;
  },
  updateSacTaxProfile: async (id: number, payload: { sacCode: string; cgstRate: number; sgstRate: number; igstRate: number; isActive: boolean }) => {
    const result = (await apiClient.put(`/tax-profiles/sac/${id}`, payload)).data;
    invalidateMasterDataTags([MASTER_DATA_TAGS.sacTaxProfiles]);
    return result;
  },
  seedSacTaxProfiles: async () => {
    const result = (await apiClient.post("/tax-profiles/sac/seed-defaults", {})).data;
    invalidateMasterDataTags([MASTER_DATA_TAGS.sacTaxProfiles]);
    return result;
  },

  // ── Commission Module ───────────────────────────────────────────────────────

  // Beneficiaries
  listCommissionBeneficiaries: async (q?: string) =>
    (await apiClient.get<{ items: any[]; total: number }>("/commission/beneficiaries", { params: { q } })).data,
  getCommissionBeneficiary: async (id: number) =>
    (await apiClient.get<any>(`/commission/beneficiaries/${id}`)).data,
  createCommissionBeneficiary: async (payload: any) =>
    (await apiClient.post<any>("/commission/beneficiaries", payload)).data,
  updateCommissionBeneficiary: async (id: number, payload: any) =>
    (await apiClient.put<any>(`/commission/beneficiaries/${id}`, payload)).data,
  deactivateCommissionBeneficiary: async (id: number) =>
    apiClient.post(`/commission/beneficiaries/${id}/deactivate`, {}),

  // Rules
  listCommissionRules: async (q?: string, activeOnly?: boolean) =>
    (await apiClient.get<{ items: any[]; total: number }>("/commission/rules", { params: { q, activeOnly } })).data,
  getCommissionRule: async (id: number) =>
    (await apiClient.get<any>(`/commission/rules/${id}`)).data,
  createCommissionRule: async (payload: any) =>
    (await apiClient.post<any>("/commission/rules", payload)).data,
  updateCommissionRule: async (id: number, payload: any) =>
    (await apiClient.put<any>(`/commission/rules/${id}`, payload)).data,
  deactivateCommissionRule: async (id: number) =>
    apiClient.post(`/commission/rules/${id}/deactivate`, {}),

  // Periods
  listCommissionPeriods: async () =>
    (await apiClient.get<{ items: any[]; total: number }>("/commission/periods")).data,
  getCommissionPeriod: async (id: number) =>
    (await apiClient.get<any>(`/commission/periods/${id}`)).data,
  createCommissionPeriod: async (payload: { year: number; month: number; remarks?: string; enteredBy?: string }) =>
    (await apiClient.post<any>("/commission/periods", payload)).data,
  generateCommissionPeriod: async (id: number, actionBy: string) =>
    (await apiClient.post<any>(`/commission/periods/${id}/generate`, { actionBy })).data,
  approveCommissionPeriod: async (id: number, actionBy: string) =>
    (await apiClient.post<any>(`/commission/periods/${id}/approve`, { actionBy })).data,
  closeCommissionPeriod: async (id: number, actionBy: string) =>
    (await apiClient.post<any>(`/commission/periods/${id}/close`, { actionBy })).data,

  // Statements
  listCommissionStatements: async (params?: { periodId?: number; beneficiaryId?: number; status?: string }) =>
    (await apiClient.get<{ items: any[]; total: number }>("/commission/statements", { params })).data,
  getCommissionStatement: async (id: number) =>
    (await apiClient.get<any>(`/commission/statements/${id}`)).data,
  approveCommissionStatement: async (id: number, actionBy: string) =>
    (await apiClient.post<any>(`/commission/statements/${id}/approve`, { actionBy })).data,
  recordCommissionPayment: async (id: number, payload: { paidBy?: string; paidOn?: string; paymentReference?: string; paymentMode?: string }) =>
    (await apiClient.post<any>(`/commission/statements/${id}/pay`, payload)).data,
  cancelCommissionStatement: async (id: number, actionBy: string) =>
    apiClient.post(`/commission/statements/${id}/cancel`, { actionBy }),
  addCommissionAdjustment: async (id: number, payload: { adjustmentType: string; amount: number; reason: string; enteredBy?: string }) =>
    (await apiClient.post<any>(`/commission/statements/${id}/adjustments`, payload)).data,
  voidCommissionAdjustment: async (adjustmentId: number, payload: { voidedBy?: string; voidReason?: string }) =>
    apiClient.post(`/commission/statements/adjustments/${adjustmentId}/void`, payload),

  // Reports
  getCommissionDashboard: async () =>
    (await apiClient.get<any>("/commission/reports/dashboard")).data,
  getCommissionMonthlyRegister: async (year: number, month: number) =>
    (await apiClient.get<{ items: any[]; total: number }>("/commission/reports/monthly-register", { params: { year, month } })).data,
  getCommissionBeneficiaryPayable: async (beneficiaryId?: number) =>
    (await apiClient.get<{ items: any[]; total: number }>("/commission/reports/beneficiary-payable", { params: { beneficiaryId } })).data,
  getCommissionPaidVsUnpaid: async () =>
    (await apiClient.get<{ items: any[]; total: number }>("/commission/reports/paid-vs-unpaid")).data,

  // ── Lorry Hire ─────────────────────────────────────────────────────────────
  listLorryHireChallans: async (params?: { page?: number; pageSize?: number; q?: string; status?: string }) =>
    (await apiClient.get<{ data: any[]; total: number }>("/lorry-hire", { params })).data,
  getLorryHireChallan: async (id: number) =>
    (await apiClient.get<any>(`/lorry-hire/${id}`)).data,
  createLorryHireChallan: async (payload: any) =>
    (await apiClient.post<any>("/lorry-hire", payload)).data,
  updateLorryHireChallan: async (id: number, payload: any) =>
    (await apiClient.put<any>(`/lorry-hire/${id}`, payload)).data,
  confirmLorryHireChallan: async (id: number) =>
    (await apiClient.post<any>(`/lorry-hire/${id}/confirm`, {})).data,
  settleLorryHireChallan: async (id: number, payload: { paymentReference?: string; paymentMode?: string; settlementDate?: string }) =>
    (await apiClient.post<any>(`/lorry-hire/${id}/settle`, payload)).data,
  cancelLorryHireChallan: async (id: number, reason?: string) =>
    (await apiClient.post<any>(`/lorry-hire/${id}/cancel`, { reason })).data,

  // ── Manifest Operations ────────────────────────────────────────────────────
  addConsignmentToManifest: async (manifestId: number, payload: { consignmentId: number; enteredBy?: string }) =>
    apiClient.post(`/transport/manifests/${manifestId}/consignments`, payload),
  removeConsignmentFromManifest: async (manifestId: number, consignmentId: number) =>
    apiClient.delete(`/transport/manifests/${manifestId}/consignments/${consignmentId}`),

  // ── Booking / Transport Reports ────────────────────────────────────────────
  getBookingReport: async (params: { year: number; month: number; destination?: string; branchId?: number }) =>
    (await apiClient.get<any>("/reports/booking", { params })).data,
  getManifestReport: async (manifestId: number) =>
    (await apiClient.get<any>(`/reports/manifest/${manifestId}`)).data,
  getConsignmentPrintReport: async (consignmentId: number) =>
    (await apiClient.get<any>(`/reports/consignment/${consignmentId}`)).data,
  getConsignmentEnquiryReport: async (consignmentId: number) =>
    (await apiClient.get<any>(`/reports/consignment-enquiry/${consignmentId}`)).data,
  getLorryHirePrintReport: async (challanId: number) =>
    (await apiClient.get<any>(`/reports/lorry-hire/${challanId}`)).data,
  searchGatePasses: async (q = "", page = 1, pageSize = 15, branchId?: number) =>
    (await apiClient.get<{ data: GatePassSummary[]; total: number }>("/gate-passes", { params: { q, page, pageSize, branchId } })).data,
  getEligibleConsignments: async (branchId?: number, q?: string) =>
    (await apiClient.get<EligibleConsignmentDto[]>("/gate-passes/eligible-consignments", { params: { branchId, q } })).data,
  createGatePass: async (payload: CreateGatePassPayload) =>
    (await apiClient.post<{ id: number; gatePassNo: string; entryDate: string }>("/gate-passes", payload)).data,
  getGatePassById: async (id: number) =>
    (await apiClient.get<any>(`/gate-passes/${id}`)).data,
  getGatePassPrintReport: async (gatePassId: number, branchId?: number) =>
    (await apiClient.get<any>(`/reports/gate-pass/${gatePassId}`, { params: { branchId } })).data,

  // ── Party CRUD aliases (used by Consignee Master and similar) ─────────────
  createParty: async (payload: any) => {
    const result = (await apiClient.post<any>("/parties", payload)).data;
    invalidateMasterDataTags([MASTER_DATA_TAGS.parties, MASTER_DATA_TAGS.partyLookups]);
    return result;
  },
  updateParty: async (id: number, payload: any) => {
    const result = (await apiClient.put<any>(`/parties/${id}`, payload)).data;
    invalidateMasterDataTags([MASTER_DATA_TAGS.parties, MASTER_DATA_TAGS.partyLookups]);
    return result;
  },
  listPartiesByType: async (type: string, params?: { page?: number; pageSize?: number; q?: string }) => {
    const page = params?.page ?? 1;
    const pageSize = params?.pageSize ?? 20;
    const response = await apiClient.get<any[]>("/parties", {
      params: { type, q: params?.q, _start: (page - 1) * pageSize, _end: page * pageSize }
    });
    return {
      data: response.data,
      total: Number(response.headers["x-total-count"] ?? response.data.length)
    };
  },

  // ── Audit Logs ──────────────────────────────────────────────────────────────
  getAuditLogs: async (params: {
    page?: number;
    pageSize?: number;
    branchId?: number;
    userId?: string;
    entityType?: string;
    action?: string;
    dateFrom?: string;
    dateTo?: string;
  }) => {
    const response = await apiClient.get<{
      total: number; page: number; pageSize: number;
      data: AuditEntryDto[];
    }>("/audit-logs", { params });
    return response.data;
  },
};
